// Expert Detailed Rating API service
// For granular component-level equipment ratings (head, shaft, flex, loft/config)

import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// Type definitions matching backend Pydantic schemas

// NEW structure: flex variants are properties of individual shafts
export interface FlexVariant {
  shaft_id: number;
  shaft_flex: string;  // Original flex (e.g., "60-S")
  standard_flex: string;  // Standardized (S, R, X)
  shaft_weight: number;
  shaft_torque: number;
  shaft_material: string;
  shaft_launch: string;
  shaft_spin: string;
  is_recommended_flex: boolean;
}

// NEW structure: shaft models with their flex variants
export interface ShaftModel {
  shaft_brand: string;
  shaft_model: string;
  flex_variants: FlexVariant[];
}

export interface DriverHeadOption {
  head_id: number;
  head_brand: string;
  head_model: string;
  head_category?: string;
  head_price?: number;
  loft_options: number[];  // Now at HEAD level
  shaft_models: ShaftModel[];  // Changed from "shafts"
}

export interface IronHeadOption {
  head_id: number;
  head_brand: string;
  head_model: string;
  head_category?: string;
  head_price?: number;
  set_config_options: string[];  // Now at HEAD level
  shaft_models: ShaftModel[];  // Changed from "shafts"
}

export interface WedgeModelOption {
  model_id: number;
  model_brand: string;
  model_name: string;
  loft_configs?: string[];  // At MODEL level
  shaft_models: ShaftModel[];  // Changed from "shafts"
}

// LEGACY interfaces (kept for backwards compatibility if needed)
export interface FlexCombination {
  flex: string;
  standard_flex: string;
  shaft_id: number;
  is_recommended: boolean;
  rating_guidance?: string;
  loft_options?: number[];
  set_config_options?: string[];
  loft_configs?: string[];
  bounce_options?: number[];
  grind_options?: string[];
}

export interface ShaftOption {
  shaft_id: number;
  shaft_brand: string;
  shaft_model: string;
  shaft_weight: number;
  shaft_torque: number;
  shaft_material: string;
  shaft_launch: string;
  shaft_spin: string;
  flex_combinations: FlexCombination[];
}

export interface DetailedDriverOptions {
  profile_id: number;
  recommended_flex: string;
  flex_options: string[];
  flex_descriptions: Record<string, string>;
  driver_heads: DriverHeadOption[];
}

export interface DetailedIronOptions {
  profile_id: number;
  recommended_flex: string;
  flex_options: string[];
  flex_descriptions: Record<string, string>;
  iron_heads: IronHeadOption[];
}

export interface DetailedWedgeOptions {
  profile_id: number;
  recommended_flex: string;
  flex_options: string[];
  flex_descriptions: Record<string, string>;
  wedge_models: WedgeModelOption[];
}

// Rating submission types

export interface DetailedDriverRating {
  driver_head_id: number;
  shaft_id: number;
  shaft_flex: string;
  loft_degrees: number;
  head_rating: number;
  shaft_model_rating: number;
  shaft_flex_rating: number;
  loft_rating: number;
  combined_rating: number;
  notes?: string;
}

export interface DetailedIronRating {
  iron_head_id: number;
  shaft_id: number;
  shaft_flex: string;
  set_configuration: string;
  head_rating: number;
  shaft_model_rating: number;
  shaft_flex_rating: number;
  set_config_rating: number;
  combined_rating: number;
  notes?: string;
}

export interface DetailedWedgeRating {
  wedge_model_id: number;
  shaft_id: number;
  shaft_flex: string;
  loft_configuration: string;
  model_rating: number;
  shaft_model_rating: number;
  shaft_flex_rating: number;
  loft_set_rating: number;
  combined_rating: number;
  bounce_degrees?: number;
  bounce_rating?: number;
  grind_type?: string;
  grind_rating?: number;
  notes?: string;
}

export interface DetailedDriverRatingSubmission {
  profile_id: number;
  ratings: DetailedDriverRating[];
}

export interface DetailedIronRatingSubmission {
  profile_id: number;
  ratings: DetailedIronRating[];
}

export interface DetailedWedgeRatingSubmission {
  profile_id: number;
  ratings: DetailedWedgeRating[];
}

export interface RatingSubmissionResponse {
  status: string;
  profile_id: number;
  expert_id: number;
  ratings_submitted: number;
  ratings: any[];
}

// Helper function to get auth headers (storage-aware: respects sessionStorage vs localStorage)
const getAuthHeaders = () => {
  const storageType = localStorage.getItem('auth_storage_type');
  const storage = storageType === 'session' ? sessionStorage : localStorage;
  const token = storage.getItem('access_token');
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  };
};

// GET Endpoints - Retrieve Rating Options

export const getDetailedDriverOptions = async (
  profileId: number,
  numHeads: number = 10,
  numShaftsPerHead: number = 10
): Promise<DetailedDriverOptions> => {
  const response = await axios.get(
    `${API_BASE_URL}/api/v1/expert/equipment-detailed/drivers`,
    {
      params: {
        profile_id: profileId,
        num_heads: numHeads,
        num_shafts_per_head: numShaftsPerHead
      },
      headers: getAuthHeaders()
    }
  );
  return response.data;
};

export const getDetailedIronOptions = async (
  profileId: number,
  numHeads: number = 10,
  numShaftsPerHead: number = 10
): Promise<DetailedIronOptions> => {
  const response = await axios.get(
    `${API_BASE_URL}/api/v1/expert/equipment-detailed/irons`,
    {
      params: {
        profile_id: profileId,
        num_heads: numHeads,
        num_shafts_per_head: numShaftsPerHead
      },
      headers: getAuthHeaders()
    }
  );
  return response.data;
};

export const getDetailedWedgeOptions = async (
  profileId: number,
  numModels: number = 10,
  numShaftsPerModel: number = 10
): Promise<DetailedWedgeOptions> => {
  const response = await axios.get(
    `${API_BASE_URL}/api/v1/expert/equipment-detailed/wedges`,
    {
      params: {
        profile_id: profileId,
        num_models: numModels,
        num_shafts_per_model: numShaftsPerModel
      },
      headers: getAuthHeaders()
    }
  );
  return response.data;
};

// POST Endpoints - Submit Ratings

export const submitDetailedDriverRatings = async (
  submission: DetailedDriverRatingSubmission
): Promise<RatingSubmissionResponse> => {
  const response = await axios.post(
    `${API_BASE_URL}/api/v1/expert/ratings/detailed/drivers`,
    submission,
    { headers: getAuthHeaders() }
  );
  return response.data;
};

export const submitDetailedIronRatings = async (
  submission: DetailedIronRatingSubmission
): Promise<RatingSubmissionResponse> => {
  const response = await axios.post(
    `${API_BASE_URL}/api/v1/expert/ratings/detailed/irons`,
    submission,
    { headers: getAuthHeaders() }
  );
  return response.data;
};

export const submitDetailedWedgeRatings = async (
  submission: DetailedWedgeRatingSubmission
): Promise<RatingSubmissionResponse> => {
  const response = await axios.post(
    `${API_BASE_URL}/api/v1/expert/ratings/detailed/wedges`,
    submission,
    { headers: getAuthHeaders() }
  );
  return response.data;
};

// ============================================================================
// Get ALL Equipment (for custom combo dropdown)
// ============================================================================

interface AllEquipmentOption {
  id: number;
  brand: string;
  model: string;
  category?: string;
  shaft_type?: string;
}

export const getAllDriverHeads = async (): Promise<AllEquipmentOption[]> => {
  const response = await axios.get(
    `${API_BASE_URL}/api/v1/expert/equipment/all-driver-heads`,
    { headers: getAuthHeaders() }
  );
  return response.data;
};

export const getAllIronHeads = async (): Promise<AllEquipmentOption[]> => {
  const response = await axios.get(
    `${API_BASE_URL}/api/v1/expert/equipment/all-iron-heads`,
    { headers: getAuthHeaders() }
  );
  return response.data;
};

export const getAllWedgeModels = async (): Promise<AllEquipmentOption[]> => {
  const response = await axios.get(
    `${API_BASE_URL}/api/v1/expert/equipment/all-wedge-models`,
    { headers: getAuthHeaders() }
  );
  return response.data;
};

export const getAllShafts = async (category: 'driver' | 'iron' | 'wedge'): Promise<AllEquipmentOption[]> => {
  const response = await axios.get(
    `${API_BASE_URL}/api/v1/expert/equipment/all-shafts`,
    {
      params: { category },
      headers: getAuthHeaders()
    }
  );
  return response.data;
};

// Draft Endpoints - in-progress work, stored server-side (M3)
//
// The payload is opaque to the backend: it is stored and returned verbatim, so its
// shape is owned entirely by the client (see useRatingDraft). That is deliberate --
// the §2A rating-surface rewrite changes what a draft contains without a migration.

export type RatingCategory = 'driver' | 'iron' | 'wedge';

export interface RatingDraft {
  payload: Record<string, any>;
  updated_at: string;
}

export interface RatingDraftsResponse {
  profile_id: number;
  drafts: Partial<Record<RatingCategory, RatingDraft>>;
}

export const saveRatingDraft = async (
  profileId: number,
  category: RatingCategory,
  payload: Record<string, any>
): Promise<{ status: string; updated_at: string }> => {
  const response = await axios.put(
    `${API_BASE_URL}/api/v1/expert/drafts`,
    { profile_id: profileId, category, payload },
    { headers: getAuthHeaders() }
  );
  return response.data;
};

export const getRatingDrafts = async (profileId: number): Promise<RatingDraftsResponse> => {
  const response = await axios.get(
    `${API_BASE_URL}/api/v1/expert/drafts`,
    { params: { profile_id: profileId }, headers: getAuthHeaders() }
  );
  return response.data;
};

// Omit `category` to discard all three categories for the profile.
export const deleteRatingDraft = async (
  profileId: number,
  category?: RatingCategory
): Promise<{ status: string; deleted: number }> => {
  const response = await axios.delete(
    `${API_BASE_URL}/api/v1/expert/drafts`,
    {
      params: category ? { profile_id: profileId, category } : { profile_id: profileId },
      headers: getAuthHeaders()
    }
  );
  return response.data;
};
